import { Resource } from '@medplum/fhirtypes';
import { getDisplayString } from '@medplum/core';

/*
 * Creates a filter function that returns only resources with a specific status.
 * Use this when you only want to show active/in-progress items.
 */
export const createActiveFilter = <T extends Resource>(
  getStatus: (resource: T) => string | undefined,
  activeValue: string = 'active'
): ((resources: T[]) => T[]) => {
  return (resources: T[]) =>
    resources.filter((resource) => getStatus(resource) === activeValue);
};

/*
 * Creates a sort function that puts active items first, then sorts alphabetically.
 * Use this when you want to show all items but prioritize active ones.
 */
export const createActiveFirstSort = <T extends Resource>(
  getStatus: (resource: T) => string | undefined,
  activeValue: string = 'active'
): ((resources: T[]) => T[]) => {
  return (resources: T[]) =>
    [...resources].sort((a, b) => {
      const aStatus = getStatus(a);
      const bStatus = getStatus(b);

      // Active items first
      if (aStatus === activeValue && bStatus !== activeValue) {
        return -1;
      }
      if (aStatus !== activeValue && bStatus === activeValue) {
        return 1;
      }

      // Then alphabetically
      return getDisplayString(a).localeCompare(getDisplayString(b));
    });
};
